import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FiCheck, FiMail } from 'react-icons/fi';
import { requestForgotPassword } from '@/controllers/auth.controller';
import useSmartToast from '@/hooks/SmartToast';
import { isEmail } from '@/utils/validate';

export default function ForgotPassword() {
  const [isLoading, setLoading] = useState(false);
  const toast = useSmartToast();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!router.isReady) return;

    if (!['admin', 'client', 'vendor'].includes(router.query.id as any))
      return toast.error(null, 'Invalid reset link. Please try again');
  }, [router.query]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isEmail(email)) return toast.error(null, 'Invalid email');

    setLoading(true);

    try {
      await requestForgotPassword({
        email,
        type: router.query.id as string,
      });
      toast.success('Password reset link was sent to your email.');
      setSent(true);
    } catch (error) {
      console.error(error);
      toast.error(error, 'Error resetting password');
      setLoading(false);
    }
  };

  const isValidToken = ['admin', 'client', 'vendor'].includes(
    router.query.id as any,
  );

  return (
    <HStack h={'100vh'} overflow='hidden'>
      <Stack
        display={{ base: 'none', md: 'flex' }}
        w='50%'
        h='100%'
        position='relative'
        color='white'
      >
        <Image
          src='/images/stock1.jpg'
          alt='Procura Background'
          w='full'
          h='full'
          objectPosition='center'
          objectFit='cover'
        />
        <Box
          position='absolute'
          p='16'
          top='0'
          left='0'
          w='full'
          h='full'
          bg='linear-gradient(to bottom, rgba(0,0,0,0.75), transparent)'
        >
          <Heading size='xl' letterSpacing='wider'>
            All Procura.
            <br />
            One Platform.
          </Heading>
        </Box>
      </Stack>
      <Stack
        w={{ base: '100%', md: '50%' }}
        h='100%'
        p='8'
        alignItems='center'
        overflowX='hidden'
        overflowY='auto'
      >
        {!sent && (
          <Stack
            w='full'
            maxW='sm'
            h='full'
            textAlign='center'
            alignItems='center'
            as='form'
            onSubmit={handleSubmit}
          >
            <Image src='/images/brand-logo.png' alt='Procura Logo' h='14' />
            {isValidToken ? (
              <>
                <Heading
                  size='lg'
                  mt='8'
                  mb='4'
                  fontWeight='semibold'
                  color='gray.800'
                >
                  Hello {router.query.id}!
                </Heading>
                <Text fontSize='md' fontWeight='medium' color='gray.500'>
                  Forgot your password? Let&apos;s get you back in.
                </Text>

                <FormControl mt='4'>
                  <FormLabel>Email</FormLabel>
                  <InputGroup size='lg'>
                    <InputLeftElement>
                      <FiMail />
                    </InputLeftElement>
                    <Input
                      placeholder='you@example.com'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>

                <Button
                  variant='procura'
                  size='lg'
                  w='full'
                  mt='4'
                  fontSize='sm'
                  py='4'
                  isLoading={isLoading}
                  type='submit'
                >
                  Send Password Reset Link
                </Button>
              </>
            ) : (
              <Stack>
                <Heading
                  size='lg'
                  mt='8'
                  mb='4'
                  fontWeight='semibold'
                  color='gray.800'
                >
                  Invalid Link
                </Heading>
                <Text fontSize='md' fontWeight='medium' color='gray.500'>
                  Invalid link. Please try again
                </Text>
              </Stack>
            )}
          </Stack>
        )}
        {sent && (
          <Stack
            w='full'
            maxW='sm'
            h='full'
            textAlign='center'
            alignItems='center'
          >
            <Image src='/images/brand-logo.png' alt='Procura Logo' h='14' />
            <Box
              rounded='full'
              width='24'
              height='24'
              borderColor='blue.500'
              borderWidth='2px'
              mt='12'
              display='flex'
              justifyContent='center'
              alignItems='center'
              color='blue.500'
            >
              <FiCheck color='blue.500' fontSize='48px' />
            </Box>
            <Heading
              size='lg'
              mt='8'
              mb='4'
              fontWeight='semibold'
              color='gray.800'
            >
              Reset Link Sent
            </Heading>
            <Text fontSize='md' fontWeight='medium' color='gray.500'>
              Password reset link was sent to your email.
              <br />
              Please check your inbox and spam folder.
            </Text>
          </Stack>
        )}
      </Stack>
    </HStack>
  );
}

ForgotPassword.getLayout = function getLayout(page) {
  return <Box>{page}</Box>;
};
